import React from 'react';
import Chatbot from 'react-chatbot-kit'
import 'react-chatbot-kit/build/main.css'
import config from './bot/config.js';
import MessageParser from './bot/MessageParser.jsx';
import ActionProvider from './bot/ActionProvider.jsx';
import { useWorkspaceContext } from '../../context/workspaceContext';

import ClearAllIcon from '@mui/icons-material/ClearAll';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button, FormControl,  InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { ResourceType } from '../../@types/resource-type';
import LinearProgress from '@mui/material/LinearProgress';
import { ChatBotActionType } from '../../@types/chatbot-action-type';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LlmService from '../../services/llm.service';
import { toast } from 'react-toastify';

const saveMessages = (messages: any): void => {
    localStorage.setItem(config.archiveName, JSON.stringify(messages));
}

const loadMessages = (): any => {
    const messages = localStorage.getItem(config.archiveName);
    return messages ? JSON.parse(messages) : [];
}

export default function ChatBotEditor(prop: { panelSizePercent: (number | string)[] }): React.ReactElement {
    const { panelSizePercent } = prop;
    const [showBot, toggleBot] = React.useState(false);
    const [includeESDChecked, setIncludeESDChecked] = React.useState(false);
    const [uploadingFile, setUploadingFile] = React.useState(false);
    const [llms, setLlms] = React.useState(['']);
    const [llm, setLlm] = React.useState('');
    const { currentWorkspaceObject, setChatBotAction } = useWorkspaceContext();
    
    React.useEffect(() => {
        toggleBot(panelSizePercent[3] !== 0)
    }, [panelSizePercent]);

    React.useEffect(() => {
        if (!showBot) {
            return;
        }
        LlmService.getLlmModels().then((response) => {
            setLlms(response.data.sort().reverse());
            setLlm(response.data[0]);
            setChatBotAction({ actionType: ChatBotActionType.LLM_MODEL, payload: response.data[0] });
            
        }).catch((error) => {
            toast.error('Error getting LLM - ' + error.message);
        });
    }, [showBot]);

    //
    // NB: The react-chatbot-kit input element only allows for single line input
    // This useEffect is used to replace the input element with a textarea element
    // This is done to allow the user to input multiple lines of text   
    // The useEffect should be removed one day when the react-chatbot-kit input element 
    // is updated to allow for multiple lines of text
    //
    React.useEffect(() => {
        let input = document.getElementsByClassName('react-chatbot-kit-chat-input')[0];
        if (!input || (input instanceof HTMLTextAreaElement)) {
            return;
        }

        const textarea = document.createElement('textarea');  
        textarea.className = 'react-chatbot-kit-chat-input';
        textarea.rows = 1;
        textarea.placeholder = 'Type a message...';
        textarea.style.maxHeight = '200px'; // Set the maximum height of the textarea to 200px
        textarea.style.resize = 'none'; // Remove the resize handle from the textarea

        const adjustTextareaHeight = (textarea: HTMLTextAreaElement): void  => {
            
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        };

        textarea.oninput = (e: any): void => {
            textarea.value = e.target.value;
            if (textarea.value.trim() === '') {
                textarea.style.height = ''; // Reset height to default when empty
            } else {
                adjustTextareaHeight(textarea);
            }
        };
        
        input.replaceWith(textarea);

        const form = document.getElementsByClassName('react-chatbot-kit-chat-input-form')[0] as HTMLInputElement;
        form.onsubmit = (e: any): void => {
            e.preventDefault();
            if (textarea.value.trim() === '') {
                return;
            }
            setChatBotAction({ actionType: ChatBotActionType.TRIGGER_REPLY, payload: textarea.value });
            textarea.value = '';
            textarea.style.height = ''; // Reset height to default when empty
        }
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const uploadFile = (): void => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (event: any): void => {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onload = (e: any): void => {
                setChatBotAction({
                    actionType: ChatBotActionType.UPLOAD_FILE,
                    payload: {
                        name: file.name,
                        contents: e.target.result
                    }
                });
            };
            reader.onloadstart = (): void => {
                setUploadingFile(true);
            };
            reader.onloadend = (): void => {
                setUploadingFile(false);
            };
            reader.readAsText(file);
            
        }
        input.click();
    }

    
    // Added showBot to trigger the Chatbot saveMessages function
    // NB: The ChatBot triggers saveMessages function only when it is unmounted.
    return (
        <div style={{height: '100%', width: '100%', backgroundColor: '#FAFEFE'}}>
            {showBot && <>
                <Chatbot
                    config={config}
                    messageParser={MessageParser}
                    actionProvider={ActionProvider}
                    saveMessages={saveMessages}
                    messageHistory={loadMessages()}

                    validator={
                        (input: string): boolean => {
                            return input.trim() === '' ? false : true;
                        }
                    }
                />
        
             
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start',  marginLeft: '5px' }}>
                    {uploadingFile && <LinearProgress color="secondary" />}
                    <FormControl size='small'>
                        <InputLabel id='llm-label'>LLM</InputLabel>
                        <Select
                            labelId='llm-label'
                            id='llm'
                            value={llms && llms.length > 0 ? llm : ''}
                            label='LLM'
                            onChange={
                                (e): void => {
                                    setLlm(e.target.value);
                                    setChatBotAction({ actionType: ChatBotActionType.LLM_MODEL, payload: e.target.value });
                                }}
                        >
                            {llms && llms.map((model: string) => (
                                <MenuItem key={model} value={model}>{model}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size='small'
                                disabled={currentWorkspaceObject && currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_TREES ? false : true}
                                checked={includeESDChecked}
                                onChange={
                                    (e): void => {
                                        setIncludeESDChecked(e.target.checked);
                                        setChatBotAction({ actionType: ChatBotActionType.ADD_ESD, payload: e.target.checked });
                                    }
                                }
                        
                            />
                        }
                        label={<Typography variant='body2'>Add ESD</Typography>}
                        labelPlacement='end'
                    />
                    <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        aria-label='clear all'
                        onClick={(): void => { setChatBotAction({ actionType: ChatBotActionType.CLEAR_HISTORY, payload: null}); }}
                        startIcon={<ClearAllIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        Clear
                    </Button>
                    {/**
                        <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        aria-label='Upload a file'
                        onClick={uploadFile}
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: 'none' }}
                        disabled
                    >
                        Upload
                </Button>
                **/}
                </div>
            </>}
        </div>
    );
}